import axios from 'axios';
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import type { ApiResponse, HttpMethod, RequestOptions } from '../types/api';
import { getToken } from './token';

// 创建默认的axios实例
const createAxiosInstance = (): AxiosInstance => {
  const instance = axios.create({
    baseURL: import.meta.env.VITE_APP_API_BASE,
    timeout: 15000,
    headers: {
      'Content-Type': 'application/json',
      'Accept-Language': import.meta.env.VITE_APP_ACCEPT_LANGUAGE
    },
  });

  // 请求拦截器
  instance.interceptors.request.use(
    async (config) => {
      // 在这里可以添加token等认证信息
      const token = await getToken();
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      // const token = localStorage.getItem('token');
      // if (token && config.headers) {
      //   config.headers.Authorization = `Bearer ${token}`;
      // }
      
      // 可以添加请求loading
      // showLoading();
      
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // 响应拦截器
  instance.interceptors.response.use(
    (response: AxiosResponse) => {
      // 隐藏loading
      // hideLoading();
      
      const res = response.data;

      // console.log("res", res);
      
      // 如果响应数据中没有 code 字段，说明是直接返回的数据
      if (!('code' in res)) {
        return response;
      }
      
      // 有 code 字段的情况，根据业务状态码处理
      if (res.code !== 0 && res.code !== 200) {
        // 统一处理错误
        console.error(res.message || res.msg || '请求出错');
        
        return Promise.reject(new Error(res.message || res.msg || '请求出错'));
      }
      
      // 修改这里，返回完整的 response
      response.data = res.data;
      return response;
    },
    (error) => {
      // 隐藏loading
      // hideLoading();
      
      // 处理HTTP状态码错误
      if (error.response) {
        const { status } = error.response;
        let message = '请求出错';
        
        switch (status) {
          case 400:
            message = '请求错误';
            break;
          case 401:
            message = '未授权，请重新登录';
            // 处理token失效
            // clearUser();
            // redirectToLogin();
            break;
          case 403:
            message = '拒绝访问';
            break;
          case 404:
            message = '请求地址出错';
            break;
          case 408:
            message = '请求超时';
            break;
          case 500:
            message = '服务器内部错误';
            break;
          case 501:
            message = '服务未实现';
            break;
          case 502:
            message = '网关错误';
            break;
          case 503:
            message = '服务不可用';
            break;
          case 504:
            message = '网关超时';
            break;
          default:
            message = `连接出错(${status})!`;
        }
        
        console.error(message);
      } else {
        if (error.message && error.message.includes('timeout')) {
          console.error('请求超时！');
        } else {
          console.error('网络连接异常！');
        }
      }
      
      return Promise.reject(error);
    }
  );

  return instance;
};

// 创建axios实例
const axiosInstance = createAxiosInstance();

// 封装请求方法
const http = {
  request<T = any>(
    method: HttpMethod,
    url: string,
    data?: any,
    options?: RequestOptions
  ): Promise<ApiResponse<T>> {
    const config: AxiosRequestConfig = {
      url,
      method,
    };

    // 处理请求参数
    if (method.toUpperCase() === 'GET' || method.toUpperCase() === 'DELETE') {
      config.params = data;
    } else {
      config.data = data;
    }

    // 合并自定义配置
    if (options) {
      if (options.timeout) {
        config.timeout = options.timeout;
      }

      if (options.headers) {
        config.headers = { ...config.headers, ...options.headers };
      }

      if (options.contentType) {
        config.headers = {
          ...config.headers,
          'Content-Type': options.contentType,
        };
      }
    }

    return axiosInstance.request(config) as Promise<ApiResponse<T>>;
  },

  get<T = any>(url: string, params?: any, options?: RequestOptions): Promise<ApiResponse<T>> {
    return this.request<T>('GET', url, params, options);
  },

  post<T = any>(url: string, data?: any, options?: RequestOptions): Promise<ApiResponse<T>> {
    return this.request<T>('POST', url, data, options);
  },

  put<T = any>(url: string, data?: any, options?: RequestOptions): Promise<ApiResponse<T>> {
    return this.request<T>('PUT', url, data, options);
  },

  delete<T = any>(url: string, params?: any, options?: RequestOptions): Promise<ApiResponse<T>> {
    return this.request<T>('DELETE', url, params, options);
  },

  patch<T = any>(url: string, data?: any, options?: RequestOptions): Promise<ApiResponse<T>> {
    return this.request<T>('PATCH', url, data, options);
  },

  // 上传文件
  uploadFile<T = any>(url: string, formData: FormData, options?: RequestOptions): Promise<ApiResponse<T>> {
    const uploadOptions: RequestOptions = {
      contentType: 'multipart/form-data',
      ...options,
    };
    return this.post(url, formData, uploadOptions);
  },
};

export default http; 